import React, { useContext, useDebugValue, useEffect, useState } from 'react'
import { getDatabase, ref, get, update, set } from 'firebase/database';

import axios from 'axios';

import MainNav from '../../Components/Navbar';
import CustomSelect from '../../Components/CustomSelect';
import GlobalContext from '../../Components/GlobalContext';
import { firebaseToString, dateStringToDateTime, dateTimeToDateString } from '../../Tools/tools';

import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import Modal from 'react-bootstrap/Modal'

import jsonRolls from './rolls_remodel.json'
import './Dashboard.css'
import DepCurrentMonthGraph from './DepCurrentMonthGraph';

import sunny_blue from './icons/sunny_blue.svg';
import pregnant_woman_blue from './icons/pregnant_woman_blue.svg';
import work_off_blue from './icons/work_off_blue.svg';

import sunny_white from './icons/sunny_white.svg';
import pregnant_woman_white from './icons/pregnant_woman_white.svg';
import work_off_white from './icons/work_off_white.svg';
import MonthToMonthGraph from './MonthToMonthGraph';

const debug_mode = false

function Dashboard() {

    const { globalData } = useContext(GlobalContext);
    const db = getDatabase()

    const [companyNames, setCompanyNames] = useState([]);
    const [allCompanies, setAllCompanies] = useState([]);
    const currentYear = new Date().getFullYear();
    const startYear = 2024;
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
    const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
    const [departmentOptions, setDepartmentOptions] = useState([]);

    const [rawData, setRawData] = useState({});

    const [interactiveDashboardData, setInteractiveDashboardData] = useState({});

    const [tipClicked, setTipClicked] = useState(true)

    const [employeesAdditionalInfo, setEmployessAdditionalInfo] = useState({})

    const namesNotToCount = ['uid', 'originalTime', 'registeredDateTime', 'countAttendance']

    useEffect(() => {
        const companiesRef = ref(db, '/companies/');
        get(companiesRef).then((snapshot) => {
            if (snapshot.exists()) {
                let companies = []
                for (const [companyName, companyData] of Object.entries(snapshot.val())) {
                    if (companyData.isCompanyVisible) {
                        if (!companies.includes(companyName)) {
                            companies.push(companyName)
                        }
                    }
                }
                companies.sort()
                setCompanyNames(companies)
                setAllCompanies(snapshot.val());
            }
        })
        if (globalData.role == "externo") {
            setSelectedCompany(globalData.company)
        }
    }, [globalData]);

    const getRollData = async () => {

        if (debug_mode) {
            console.log("debug data")
            return jsonRolls[Number(selectedYear)][Number(selectedMonth)][selectedCompany] ?? null
            // return jsonRolls
        } else {
            console.log("firebase data")
            const rollsRef = ref(db, `/rolls_remodel/${Number(selectedYear)}/${Number(selectedMonth)}/${selectedCompany}`)
            if (selectedCompany) {
                const snapshot = await get(rollsRef)
                if (snapshot.exists()) {
                    setRawData(snapshot.val())
                    return snapshot.val()
                } else {
                    return null
                }
            }
        }
    }

    /**
     * Converts a JavaScript Date object to a formatted string "dd-mm-yyyy-hh:mm:ss".
     *
     * @param {Date} date - The date object to format.
     * @returns {string} The formatted date string.
     */
    function dateTimeToFancyDateString(date, showTime = false) {
        date = new Date(date)
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const sec = String(date.getSeconds()).padStart(2, '0');
        if (showTime) {
            return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
        } else {
            return `${dd}/${mm}/${yyyy}`;
        }
    }

    function calculateOverallAtendence(overallDepAtendence) {
        const values = Object.values(overallDepAtendence)

        let acc = 0
        let count = 0

        values.forEach((value) => {
            acc += value
            count += 1
        })

        return ((acc / count) * 100).toFixed(0)
    }

    function parseNumber(value) {
        return Math.round(
            parseFloat(value.toString().replace(',', '.'))
        )
    }

    function parsePresentAndTotal(present, total) {
        present = parseNumber(present)
        total = parseNumber(total)

        if (total < present) {
            total = present
        }

        return [present, total]
    }

    /**
     * Parses the given additional information and returns an array containing:
     * - the original additionalInfo string,
     * - the corresponding icon,
     * - and a short code.
     *
     * @param {string} additionalInfo - The additional information to parse (e.g., 'FÉRIAS', 'AFASTADO', 'LICENÇA MATERNIDADE').
     * @returns {[string, any, string]} An array with [additionalInfo, icon, shortCode].
     */
    function parseAdditionalInfo(additionalInfo) {
        let icon = sunny_blue
        let short = "FE"

        switch (additionalInfo) {
            case 'FÉRIAS':
                icon = sunny_blue
                short = "FE"
                break;
            case 'AFASTADO':
                icon = work_off_blue
                short = "AF"
                break;
            case 'LICENÇA MATERNIDADE':
                icon = pregnant_woman_blue
                short = "LM"
                break;
            default:
                icon = sunny_blue
                break;
        }

        return [additionalInfo, icon, short]
    }

    /**
     * Returns a string representing additional information based on the selected observation value.
     *
     * @param {number} selectedObs - The selected observation code (0: FÉRIAS, 1: AFASTADO, 2: LICENÇA MATERNIDADE).
     * @returns {string} The corresponding additional information string.
     */
    function selectedObsToAdditionalInfo(selectedObs) {
        if (selectedObs === 0) {
            return 'FÉRIAS'
        } else if (selectedObs === 1) {
            return 'AFASTADO'
        } else if (selectedObs === 2) {
            return 'LICENÇA MATERNIDADE'
        } else {
            return 'FÉRIAS'
        }
    }

    function hasAdditionalInfo(employee, additionalInfo) {
        return additionalInfo[employee] !== undefined
    }

    function calculatePercentageWithBonus(a, b) {
        // Handle edge cases
        if (b === 0) {
            return 1; // Return max value if division by zero
        }

        // Calculate base percentage (a/b)
        const basePercentage = a / b;

        // Add 5% bonus (multiply by 1.05)
        const withBonus = basePercentage * 1.05;

        // Cap at 1 (100%)
        return Math.min(withBonus, 1);
    }

    function additionalInfoDate(dateStr) {
        const [yyyy, mm, dd] = dateStr.split("-");
        return `${dd}/${mm}/${yyyy}`;
    }

    /**
     * Parses a date string in the format "dd/mm/yyyy" and returns a Date object set to 10:00 local time.
     *
     * @param {string} daysOff - The date string in "dd/mm/yyyy" format.
     * @returns {Date} The parsed Date object.
     */
    function parseDaysOff(daysOff) {
        const [day, month, year] = daysOff.split('/')

        let parsedDate = new Date(`${year}-${month}-${day}T10:00`)

        return parsedDate
    }

    const validKeys = [
        "coordinates",
        "dias",
        "herança",
        "originalTime",
        "registeredDateTime",
        "sessionRoll",
        "userId",
    ]

    function validRollKeys(rollKeys) {
        return validKeys.every(item => rollKeys.includes(item))
    }

    function isDateBetween(start, date, end) {
        start = new Date(start)
        date = new Date(date)
        end = new Date(end)
        return date >= start && date <= end
    }

    function getTodaysDate(offsetDays = 0) {
        const date = new Date();
        date.setDate(date.getDate() + offsetDays);
        return date.toISOString().split('T')[0];
    }

    const createDashboard = async () => {
        let selectedDate = new Date()
        selectedDate.setMonth(selectedMonth - 1)
        selectedDate.setFullYear(selectedYear)

        const allRolls = await getRollData()

        const employees = {}

        const employeesAdditionalInfo = {}

        if (allRolls) {
            setTipClicked(false)
            for (const [depName, rolls] of Object.entries(allRolls)) {

                employees[depName] = {}

                let allDates = [] // [1, 3, 5, 6]
                let allRollDates = [] // [Date(2025, 11, 2), Date(2025, 11, 3), Date(2025, 11, 5)]
                let updatedRolls = {} // {Date(): rollInfo}

                for (let [rollDate, rollInfo] of Object.entries(rolls)) {
                    rollDate = dateStringToDateTime(rollDate)

                    if (!allDates.includes(rollDate.getDate())) {
                        allDates.push(rollDate.getDate())
                    }

                    allRollDates.push(rollDate)

                    if (validRollKeys(Object.keys(rollInfo))) {
                        updatedRolls[rollDate] = rollInfo
                    }
                }

                allDates.sort((a, b) => a - b)
                allRollDates.sort((a, b) => new Date(a) - new Date(b))

                let originalTime_registeredDatetime = {}

                // Buscando somente a ultima chamda registrada para cada originalTime
                for (const [registered, roll] of Object.entries(updatedRolls)) {
                    const originalTime = dateStringToDateTime(roll.originalTime)
                    originalTime_registeredDatetime[originalTime] = registered
                }

                Object.values(originalTime_registeredDatetime).forEach((rollDate) => {
                    let originalTime = updatedRolls[rollDate]['originalTime']
                    originalTime = dateStringToDateTime(originalTime)

                    let uOriginalTime = new Date(originalTime.getTime())
                    uOriginalTime.setHours(10, 0, 0, 0)

                    if (!Object.keys(employees[depName]).some(d => new Date(d).toISOString() === uOriginalTime.toISOString())) {
                        employees[depName][uOriginalTime] = {}
                    }

                    const rollDateKeys = Object.keys(updatedRolls[rollDate])
                    if (rollDateKeys.includes('employees')) {
                        let empList = updatedRolls[rollDate]['employees']

                        const countAttendance = updatedRolls[rollDate]['countAttendance'] ?? true

                        employees[depName][uOriginalTime]['countAttendance'] = countAttendance

                        empList.forEach((e) => {
                            const employeeName = e.employee.toString().trim().replaceAll('  ', ' ')

                            if (!countAttendance) {
                                employees[depName][uOriginalTime][employeeName] = 'FALTA'
                            } else if (Object.keys(employees[depName][uOriginalTime]).includes(employeeName)) {
                                if (e.status === 'PRESENTE') {
                                    employees[depName][uOriginalTime][employeeName] = e.status
                                }
                            } else {
                                employees[depName][uOriginalTime][employeeName] = e.status
                            }

                            const employeeKeys = Object.keys(e)

                            if (employeeKeys.includes("additionalInfo") &&
                                employeeKeys.includes("daysOffBeginning") &&
                                employeeKeys.includes("daysOffEnding")) {
                                const [additionalInfo, additionalInfoIconPath, additionalInfoShort] = parseAdditionalInfo(e["additionalInfo"])

                                if (!Object.keys(employeesAdditionalInfo).includes(employeeName)) {
                                    employeesAdditionalInfo[employeeName] = []
                                }

                                employeesAdditionalInfo[employeeName].push({
                                    'additionalInfo': additionalInfo,
                                    'additionalInfoIconPath': additionalInfoIconPath,
                                    'daysOffBeginning': parseDaysOff(e['daysOffBeginning']),
                                    'daysOffEnding': parseDaysOff(e['daysOffEnding']),
                                    'additionalInfoShort': additionalInfoShort,
                                    'registeredDateTime': rollDate
                                })
                            }
                        })


                    } else if (rollDateKeys.includes('totalofPeople')) {
                        let present = updatedRolls[rollDate]['presentPeople'] ?? 0;
                        let total = updatedRolls[rollDate]['totalofPeople'] ?? 0;

                        [present, total] = parsePresentAndTotal(updatedRolls[rollDate]['presentPeople'], updatedRolls[rollDate]['totalofPeople']);

                        if (Object.keys(employees[depName][uOriginalTime]).includes('totalofPeople')) {
                            employees[depName][uOriginalTime]['presentPeople'] += present
                            employees[depName][uOriginalTime]['totalofPeople'] += total
                        } else {
                            employees[depName][uOriginalTime]['presentPeople'] = present
                            employees[depName][uOriginalTime]['totalofPeople'] = total
                        }
                    }

                    employees[depName][uOriginalTime]['uid'] = updatedRolls[rollDate]['userId']
                    employees[depName][uOriginalTime]['originalTime'] = originalTime.toISOString()
                    employees[depName][uOriginalTime]['registeredDateTime'] = rollDate
                })
            }
        }

        setEmployessAdditionalInfo(employeesAdditionalInfo);

        setInteractiveDashboardData(employees);
    }

    return <>
        <MainNav />
        <Container>
            <div className='d-flex align-items-center'>
                <Options />
                {tipClicked && <i className="ms-3 bi bi-arrow-90deg-left"></i>}

            </div>
            {tipClicked && <Tip />}
            <InteractiveDashboard interactiveDashboardData={interactiveDashboardData} employeesAdditionalInfo={employeesAdditionalInfo} />
        </Container>
    </>

    function Tip() {
        return <>
            <h5>Selecione o mês e ano desejado e clique em "Gerar Dashboard"</h5>
        </>
    }

    function Options() {
        return <div className="my-3 d-flex flex-wrap">
            {/*filtros, este primeiro aparece exclusivamente se a pessoa for admin*/}
            {globalData.role === 'admin' && (
                <CustomSelect
                    options={companyNames
                        .filter((name) => name !== 'id')
                        .map((name) => ({ value: name, label: firebaseToString(name) }))}
                    value={selectedCompany}
                    onChange={(e) => {
                        const companyValue = e.target.value;
                        setSelectedCompany(companyValue);
                        setSelectedDepartment('');
                        setInteractiveDashboardData([])
                        const companyData = allCompanies[companyValue];
                        if (companyData) {
                            const deps = Object.keys(companyData)
                                .filter((key) => key !== 'isCompanyVisible' && key !== 'instrutores');
                            setDepartmentOptions(
                                deps.map(dep => ({ value: dep, label: firebaseToString(dep) }))
                            );
                        } else {
                            setDepartmentOptions([]);
                        }
                    }}
                    placeholder="Empresa"
                    ariaLabel="select-company"
                    className='me-3'
                    style={{ width: '300px' }} />
            )}

            <CustomSelect
                options={departmentOptions}
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                placeholder="Departamento"
                ariaLabel="select-department"
                className='me-3'
                style={{ width: '200px', display: 'none' }} />

            <CustomSelect
                options={Array.from({ length: 12 }, (_, i) => {
                    const value = String(i + 1).padStart(2, '0');
                    const label = new Date(0, i).toLocaleString('pt-BR', { month: 'long' });
                    return { value, label: label.charAt(0).toUpperCase() + label.slice(1) };
                })}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                placeholder="Mês"
                ariaLabel="select-month"
                className="me-3"
                style={{ width: '120px' }} />

            <CustomSelect
                options={Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
                    const year = startYear + i;
                    return { value: String(year), label: String(year) };
                })}
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                placeholder="Ano"
                ariaLabel="select-year"
                className='me-3'
                style={{ width: '80px' }} />

            <Button onClick={() => createDashboard()}>
                Gerar Dashboard
            </Button>
        </div>;
    }

    function InteractiveDashboard(props) {
        const [dashboardData, setDashboardData] = useState(props.interactiveDashboardData)
        const [additionalInfo, setAdditionalInfo] = useState(props.employeesAdditionalInfo)

        const deps = Object.keys(dashboardData);

        const [zerosVisible, setZerosVisible] = useState(true)
        const [additionalInfoIconsVisible, setAdditionalInfoIconsVisible] = useState(true)

        const [clickedDep, setClickedDep] = useState("")
        const [depDates, setDepDates] = useState([])
        const [latestDates, setLatestDates] = useState({})
        const [employees, setEmployess] = useState([])
        const [totalFrequency, setTotalFrequency] = useState(0)
        const [totalPresentPerDay, setTotalPresentPerDay] = useState({})
        const [totalPercent, setTotalPercent] = useState(0)

        const [flatRollData, setFlatRollData] = useState({})

        const [numericRoll, setNumericRoll] = useState(false)

        const [instructors, setInstructors] = useState({})
        const [selectedInstructor, setSelectedInstructor] = useState('blSnCyee52Tmv98Tx192W4d6NlI3')

        const [showEditingMenu, setShowEditingMenu] = useState(false)
        const [editingMode, setEditingMode] = useState(false)
        const [hasChanges, setHasChanges] = useState(false)
        const [datesChanged, setDatesChanged] = useState([])

        // Observation Editing Modal Variables
        const [obsEditModalVisible, setObsEditModalVisible] = useState(false);
        const [selectedObs, setSelectedObs] = useState(0)

        const [minSelectedDate, setMinSelectedDate] = useState(getTodaysDate())
        const [maxSelectedDate, setMaxSelectedDate] = useState(getTodaysDate(1))
        const [observationDate, setObservationDate] = useState(undefined)
        const [selectedEmployee, setSelectedEmployee] = useState(undefined)

        const closeObsEditModal = () => setObsEditModalVisible(false);
        const showObsEditModal = () => {
            setMinSelectedDate(getTodaysDate())
            setMaxSelectedDate(getTodaysDate(1))
            setSelectedObs(0)
            setObsEditModalVisible(true)
        };

        const saveObs = async () => {
            let path = `/rolls_remodel/${Number(selectedYear)}/${Number(selectedMonth)}/${selectedCompany}/${clickedDep}/${observationDate}/employees`;

            let employees = rawData[clickedDep][observationDate]['employees']

            for (let i = 0; i < employees.length; i++) {
                const e = employees[i];

                if (e.employee === selectedEmployee) {
                    const additionalInfoReason = selectedObsToAdditionalInfo(selectedObs)

                    employees[i] = {
                        employee: e.employee,
                        status: e.status,
                        additionalInfo: additionalInfoReason,
                        daysOffBeginning: additionalInfoDate(minSelectedDate),
                        daysOffEnding: additionalInfoDate(maxSelectedDate)

                    }

                    const newAdditionalInfo = {
                        additionalInfo: additionalInfoReason,
                        daysOffBeginning: parseDaysOff(additionalInfoDate(minSelectedDate)),
                        daysOffEnding: parseDaysOff(additionalInfoDate(maxSelectedDate)),
                        additionalInfoShort: parseAdditionalInfo(additionalInfoReason)[2],
                        additionalInfoIconPath: parseAdditionalInfo(additionalInfoReason)[1],
                        registeredDateTime: observationDate
                    }

                    setAdditionalInfo(prev => ({
                        ...prev,
                        [selectedEmployee]: [...(prev[selectedEmployee] || []), newAdditionalInfo]
                    }))
                }
            }

            // Database update
            if (!debug_mode) {
                await set(ref(db, path), employees)
                console.log(path)
            } else {
                console.log(path)
                console.log(employees)
            }

            // Force table refresh
            setDashboardData(prev => ({ ...prev }))

            closeObsEditModal()
        }

        // Observation exclusion Modal Variables
        const [obsDelModalVisible, setObsDelModalVisible] = useState(false)
        const [selectedAdditionalInfo, setSelectedAdditionalInfo] = useState({})
        const [delObservationDate, setDelObservationDate] = useState(undefined)
        const closeObsDelModal = () => setObsDelModalVisible(false);
        const showObsDelModel = (employee, additionalInfo) => {
            console.log(additionalInfo)
            const registeredDateTime = dateTimeToDateString(additionalInfo[0].registeredDateTime)
            setDelObservationDate(registeredDateTime)

            setSelectedAdditionalInfo(additionalInfo[0])
            setSelectedEmployee(employee)
            setObsDelModalVisible(true)
        };

        const delObs = async () => {
            let path = `/rolls_remodel/${Number(selectedYear)}/${Number(selectedMonth)}/${selectedCompany}/${clickedDep}/${delObservationDate}/employees`;

            let employees = rawData[clickedDep][delObservationDate]['employees']

            for (let i = 0; i < employees.length; i++) {
                const e = employees[i];

                if (e.employee === selectedEmployee) {
                    delete employees[i].additionalInfo
                    delete employees[i].daysOffBeginning
                    delete employees[i].daysOffEnding
                }
            }

            // Database update
            if (!debug_mode) {
                await set(ref(db, path), employees)
                console.log(path)
                console.log(employees)
            } else {
                console.log(path)
                console.log(employees)
            }

            setAdditionalInfo(prev => {
                const updated = { ...prev };
                if (updated[selectedEmployee]) {
                    updated[selectedEmployee] = updated[selectedEmployee].filter(info =>
                        dateTimeToDateString(info.registeredDateTime) !== delObservationDate
                    );
                    if (updated[selectedEmployee].length === 0) {
                        delete updated[selectedEmployee];
                    }
                }
                return updated;
            });

            await createDashboard()

            closeObsDelModal()
        }

        const clickedRoll = (selectedCompany, clickedDep, date, employee, uid) => {
            if (!datesChanged.some(d => d.getTime() === date.getTime())) {
                setDatesChanged(prev => [...prev, date])
            }

            date = new Date(date)
            const employeePresent = flatRollData[date.getDate()][employee]
            const day = date.getDate()
            setFlatRollData(prev => ({
                ...prev,
                [day]: {
                    ...prev[day],
                    [employee]: !employeePresent
                }
            }))

            setHasChanges(true)
        }

        function generateRandomFirebaseKey(length = 17) {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            for (let i = 0; i < length; i++) {
                const randomIndex = Math.floor(Math.random() * chars.length);
                result += chars[randomIndex];
            }
            return result;
        }

        const sendNewRollToDb = async (path, newRoll, date) => {
            const db = getDatabase()

            let newEmployees = {}

            const numericRoll = Object.keys(flatRollData[date.getDate()]).includes("presentPeople")

            if (numericRoll) {
                for (const [employee, status] of Object.entries(flatRollData[date.getDate()])) {
                    if (employee !== "uid") {
                        newEmployees[employee] = status
                        newRoll[employee] = status
                    }
                }
                delete newRoll.employees
            } else {
                newEmployees = flatRollData[date.getDate()]
                newEmployees["originalTime"] = date
            }

            if (!debug_mode) {
                await update(ref(db, path), newRoll)
            } else {
                console.log(path)
                console.log(newRoll)
            }

            setDashboardData(prev => ({
                ...prev,
                [clickedDep]: {
                    ...prev[clickedDep],
                    [date]: newEmployees
                }
            }))

            setEditingMode(false)
            setHasChanges(false)
        }

        const createNewRoll = () => {
            const now = new Date()

            for (let date of datesChanged) {
                date = new Date(date)

                const uid = flatRollData[date.getDate()]["uid"]

                if (uid) {
                    let dateForKey = new Date(date)
                    dateForKey = dateTimeToDateString(dateForKey)

                    let newOriginalTime = dashboardData[clickedDep][date]['originalTime']
                    newOriginalTime = new Date(newOriginalTime)
                    newOriginalTime.setDate(date.getDate())

                    newOriginalTime = dateTimeToDateString(newOriginalTime)
                    const newRegisteredTime = dateTimeToDateString(now)

                    let newEmployees = []
                    Object.entries(flatRollData[date.getDate()])
                        .forEach(([employee, status]) => {
                            if (employee !== "uid") {
                                newEmployees.push({
                                    employee: employee,
                                    status: status ? "PRESENTE" : "FALTA"
                                })
                            }
                        })

                    const diasKey = `-OW${generateRandomFirebaseKey()}`
                    let path = `/rolls_remodel/${Number(selectedYear)}/${Number(selectedMonth)}/${selectedCompany}/${clickedDep}/${newRegisteredTime}`
                    let newRollData = {
                        sessionRoll: 2,
                        registeredDateTime: newRegisteredTime,
                        originalTime: newOriginalTime.slice(0, 16),
                        employees: newEmployees,
                        herança: "",
                        dias: { [diasKey]: newOriginalTime.slice(0, 10).replaceAll("-", "/") },
                        coordinates: "0.0, 0.0",
                        userId: uid
                    }

                    sendNewRollToDb(path, newRollData, date)
                }
            }
        }

        function TableLegend() {
            return <>
                <span className='me-2'><b>FE:</b> Férias</span>
                <span className='me-2'><b>AF:</b> Afastado</span>
                <span className='me-2'><b>LM:</b> Licença maternidade</span>
            </>
        }

        function AdministrativeFunctions() {
            function getLastDayOfMonth() {
                return new Date(selectedYear, selectedMonth, 0).getDate()
            }

            const minDate = `${selectedYear}-${selectedMonth}-01`
            const maxDate = `${selectedYear}-${selectedMonth}-${getLastDayOfMonth()}`

            const [selectedDate, setSelectedDate] = useState(undefined);

            const [showInstructorError, setShowInstructorError] = useState(false)
            const [showDateError, setShowDateError] = useState(false)

            const [shouldCreateNewRoll, setShouldCreateNewRoll] = useState(false)

            useEffect(() => {
                if (shouldCreateNewRoll) {
                    createNewRoll()
                    setShouldCreateNewRoll(false)
                }

            }, [datesChanged])

            useEffect(() => {
                if (clickedDep) {
                    const clickedDepValues = Object.values(dashboardData[clickedDep])
                    if (clickedDep.length > 0) {
                        const firstClickedDepValue = clickedDepValues[0]
                        if (Object.keys(firstClickedDepValue).includes('registeredDateTime')) {
                            setObservationDate(dateTimeToDateString(firstClickedDepValue.registeredDateTime))
                        }
                    }
                }
            }, [clickedDep, dashboardData])

            const addNewDate = () => {
                setShowInstructorError(false)
                setShowDateError(false)

                const isValidInstructor = selectedInstructor && selectedInstructor !== "Selecionar Professor"
                const isValidDate = selectedDate !== undefined && selectedDate !== ""

                if (!isValidInstructor) { setShowInstructorError(true) }
                if (!isValidDate) { setShowDateError(true) }

                if (!isValidDate || !isValidInstructor) {
                    return
                }

                const [year, month, day] = selectedDate.split('-')
                let selectedDatetime = new Date(`${year}-${month}-${day}T10:00`)

                const newDay = selectedDatetime.getDate()
                const newFlatData = {}

                if (!depDates.includes(selectedDatetime.getDate())) {
                    let [date, roll] = Object.entries(interactiveDashboardData[clickedDep])[0]
                    let updatedRoll = { ...roll } // Copiando
                    const updatedRollKeys = Object.keys(updatedRoll)

                    if (updatedRollKeys.includes('presentPeople') && updatedRollKeys.includes('totalofPeople')) {
                        updatedRoll['presentPeople'] = 0
                        updatedRoll['totalofPeople'] = 0
                        updatedRoll['originalTime'] = selectedDatetime

                        newFlatData['presentPeople'] = 0
                        newFlatData['totalofPeople'] = 0
                    } else {
                        for (const [employee, status] of Object.entries(updatedRoll)) {
                            if (status === "PRESENTE") {
                                updatedRoll[employee] = "FALTA"
                            }
                            if (employee === "originalTime") {
                                updatedRoll["originalTime"] = selectedDatetime
                            }
                        }

                        for (const [employee, status] of Object.entries(updatedRoll)) {
                            if (employee !== "originalTime" && employee !== "registeredDateTime") {
                                newFlatData[employee] = false // Default to absent
                            }
                        }
                    }

                    updatedRoll["uid"] = selectedInstructor
                    newFlatData["uid"] = selectedInstructor

                    let updatedDepDates = [...depDates]
                    updatedDepDates.push(selectedDatetime.getDate())
                    updatedDepDates.sort((a, b) => a - b)

                    let updatedLatestDates = [...latestDates]
                    updatedLatestDates.push(selectedDatetime)
                    updatedLatestDates.sort((a, b) => new Date(a) - new Date(b))

                    setLatestDates(updatedLatestDates)
                    setDepDates(updatedDepDates)
                    setDashboardData(prev => ({
                        ...prev,
                        [clickedDep]: {
                            ...prev[clickedDep],
                            [selectedDatetime]: updatedRoll
                        }
                    }));

                    setFlatRollData(prev => ({
                        ...prev,
                        [newDay]: newFlatData
                    }))

                    setDatesChanged(prev => {
                        if (!prev.some(d => d.toISOString() === selectedDatetime.toISOString())) {
                            setShouldCreateNewRoll(true)
                            return [...prev, selectedDatetime];
                        }
                        return prev;
                    });

                } else {
                    console.log("Data existentente")
                }
            }

            return <Row className='mb-3 align-items-center'>
                <Col sm={1} className=''>
                    <div className='fw-bold'>Opções:</div>
                </Col>
                {showEditingMenu && <>
                    <Col sm={2}>
                        <Form.Check
                            type="switch"
                            id="editingModeSwitch"
                            label="Ícones Visíveis"
                            checked={additionalInfoIconsVisible}
                            onChange={(e) => {
                                setAdditionalInfoIconsVisible(e.target.checked);
                            }} />
                    </Col>
                    <Col className='d-flex justify-content-start align-items-center' sm={3}>
                        <Form.Check
                            type="switch"
                            id="editingModeSwitch"
                            label="Modo de Edição"
                            checked={editingMode}
                            onChange={(e) => {
                                setEditingMode(e.target.checked);
                            }} />
                        <Button className='ms-3' size='sm' variant='success'
                            onClick={() => createNewRoll()}
                            disabled={!hasChanges}>
                            Salvar Edições
                        </Button>
                    </Col>
                    <Col className='d-flex justify-content-start align-items-center' sm={6}>
                        <Form.Group className='d-flex'>
                            {/* <Form.Select
                                onChange={(e) => setSelectedInstructor(e.target.value)}
                                value={selectedInstructor}
                                isInvalid={showInstructorError}>
                                <option>Selecionar Professor</option>
                                {Object.entries(instructors).map((value, index) => {
                                    const [uid, name] = value
                                    return <option value={uid}>
                                        {name}
                                    </option>
                                })}
                            </Form.Select> */}
                            <Form.Control
                                type='date'
                                value={selectedDate}
                                min={minDate}
                                max={maxDate}
                                isInvalid={showDateError}
                                onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                    if (e.target.value) {
                                        setShowDateError(false)
                                    }
                                }}
                            />
                        </Form.Group>
                        <Button className='ms-3' size='sm' variant='success' onClick={() => addNewDate()}>
                            Adicionar Data
                        </Button>
                    </Col>
                </>
                }
            </Row>;
        }

        function NumericEditingField(date, key) {
            return <Form.Control
                type="number"
                style={{
                    textAlign: 'center',
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    appearance: 'textfield',
                    padding: '4px',
                    background: '#bfdefdff'
                }}
                className="no-spinner"
                key={date.toISOString()}
                value={flatRollData[date.getDate()][key]}
                onChange={(e) => {
                    setHasChanges(true);
                    setDatesChanged(prev => {
                        if (!prev.some(d => d.toISOString() === date.toISOString())) {
                            return [...prev, date];
                        }
                        return prev;
                    });
                    setFlatRollData((prev) => ({
                        ...prev,
                        [date.getDate()]: {
                            ...prev[date.getDate()],
                            [key]: Number(e.target.value)
                        }
                    }));
                }}
            >
            </Form.Control>;
        }

        const setRollDates = (dep) => {
            // Processando dados de chamada nominal
            let tempDate = []
            let tempLatestDates = {}
            let tempEmployees = []
            let tempTotalPresent = {}
            let flatData = {}
            let tempNumericTotal = 0
            let tempNumericExpected = 0
            let tempNumeric = false
            let uids = []

            // Buscando os dias unicos
            let allDates = Object.keys(dashboardData[dep])

            allDates.sort((a, b) => new Date(a) - new Date(b))

            for (let date of allDates) {
                date = new Date(date);
                const day = date.getDate()

                // Buscando total de presencas por dia
                tempTotalPresent[day] = {}
                flatData[day] = {}

                const depDateKeys = Object.keys(dashboardData[dep][date])
                if (depDateKeys.includes("presentPeople") && depDateKeys.includes("totalofPeople")) {
                    setNumericRoll(true)
                    tempNumeric = true
                    tempTotalPresent[day] = dashboardData[dep][date]
                } else {
                    setNumericRoll(false)
                }

                // Listando funcionários
                for (const [employee, status] of Object.entries(dashboardData[dep][date])) {
                    if (employee === "uid") {
                        flatData[day]["uid"] = status
                        if (!uids.includes(status)) {
                            uids.push(status)
                        }
                    }
                    if (tempNumeric) {
                        let num = parseInt(status)
                        if (employee === "presentPeople") {
                            tempNumericTotal += num
                            flatData[day]['presentPeople'] = num
                        } else if (employee === "totalofPeople") {
                            tempNumericExpected += num
                            flatData[day]['totalofPeople'] = num
                        }
                    } else if (!namesNotToCount.includes(employee)) {
                        let count = true

                        if (employeesAdditionalInfo[employee]) {
                            for (const empAddInfo of Object.values(employeesAdditionalInfo[employee])) {
                                if (isDateBetween(empAddInfo.daysOffBeginning, date, empAddInfo.daysOffEnding)) {
                                    count = false
                                }
                            }
                        }

                        if (count) {
                            flatData[day][employee] = status === "PRESENTE" || status === true
                            if (status === "PRESENTE" || status === true) {
                                tempTotalPresent[day][employee] = status
                            }
                        }
                        if (!tempEmployees.includes(employee)) {
                            tempEmployees.push(employee)
                        }
                    }
                }

                // Buscando presença mais atual
                tempLatestDates[day] = date

                // Buscando dias de aula
                if (!tempDate.includes(date.getDate())) {
                    tempDate.push(date.getDate())
                }
            }

            setFlatRollData(flatData)

            setShowGeneralReport(false)

            setClickedDep(dep)

            setTotalPresentPerDay(tempTotalPresent)

            let total = 0
            for (const v of Object.values(tempTotalPresent)) {
                total = total + Object.keys(v).length
            }

            setTotalFrequency(total)

            tempDate.sort((a, b) => a - b);
            setDepDates(tempDate);

            tempLatestDates = Object.values(tempLatestDates)
            setLatestDates(tempLatestDates)

            tempEmployees.sort()
            setEmployess(tempEmployees)

            // Calculo de percentual antigo
            // setTotalPercent(((total / (tempLatestDates.length * tempEmployees.length)) * 100).toFixed())

            // Calculando subtotal 
            // Total de dias com presenças > 1 / Total de alunos - 5%(tolerancia para férias e outras ausencias)

            let totalPresent = []

            for (const [date, present] of Object.entries(tempTotalPresent)) {
                for (const std of Object.keys(present)) {
                    if (!totalPresent.includes(std)) totalPresent.push(std)
                }
            }

            // Calculo novo
            let totalPercent = ((totalPresent.length / (tempEmployees.length * 0.95)) * 100).toFixed()
            totalPercent = totalPercent >= 100 ? 100 : totalPercent
            setTotalPercent(totalPercent)

            setShowEditingMenu(true)

            setHasChanges(false)
            setDatesChanged([])
            setEditingMode(false)

            // // Buscando dados dos professores 
            let config = {
                method: 'post',
                maxBodyLength: Infinity,
                url: `${globalData.apiHost}/users`,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': process.env.REACT_APP_BACKEND_SECRET
                },
                data: uids
            };

            axios.request(config)
                .then((response) => {
                    const teachers = response.data.users;

                    let a = {}

                    for (const teacherData of Object.values(teachers)) {
                        let firstAndLastName = teacherData.displayName
                        firstAndLastName = firstAndLastName.split(" ")
                        firstAndLastName = `${firstAndLastName[0]} ${firstAndLastName.at(-1)}`

                        a[teacherData.uid] = firstAndLastName
                    }
                    setInstructors(a)
                }).catch((error) => {
                    setInstructors({})
                })
        }

        const [showGeneralReport, setShowGeneralReport] = useState(false)
        const [allRolls, setAllRolls] = useState({})
        const [allDates, setAllDates] = useState({})
        const [totalPresent, setTotalPresent] = useState(0)
        const [totalEmployees, setTotalEmployees] = useState(0)
        const [totalPresentPerDep, setTotalPresentPerDep] = useState({})
        const [overallTotalPresent, setOverallTotalPresent] = useState(0)
        const [overallDepAtendence, setOverallDepAtendence] = useState({})

        const setGeral = () => {
            let tempAllRolls = {}
            let tempAllDates = []
            let rollsPercentage = {}
            let tempTotalEmployees = 0
            let tempPresentEmployees = 0

            for (const [dep, dates] of Object.entries(dashboardData)) {
                tempAllRolls[dep] = {}
                let datesKeys = Object.keys(dates)
                let latestDates = {}

                datesKeys.sort((a, b) => new Date(a) - new Date(b))

                for (let date of datesKeys) {
                    date = new Date(date)
                    latestDates[date.getDate()] = date

                    if (!tempAllDates.includes(date.getDate())) {
                        tempAllDates.push(date.getDate())
                    }
                }

                for (let date of Object.values(latestDates)) {
                    tempAllRolls[dep][date] = dates[date]
                }
            }

            tempAllDates.sort((a, b) => a - b)

            Object.entries(tempAllRolls).map(([depName, rolls], index) => {
                let normalizedRoll = {}

                for (const [rollDate, roll] of Object.entries(rolls)) {

                    if (Object.values(normalizedRoll).length == 0) {
                        Object.keys(roll).forEach((a) => {
                            if (a !== 'presentPeople' && a !== 'totalofPeople') {
                                normalizedRoll[a] = "FALTA"
                            }
                        })
                    }

                    namesNotToCount.forEach((name) => delete normalizedRoll[name])

                    for (const [employee, status] of Object.entries(roll)) {
                        if (!namesNotToCount.includes(employee)) {
                            let between = false

                            const employeeAdditionalInfo = additionalInfo[employee]

                            if (employeeAdditionalInfo) {
                                for (const addInfo of employeeAdditionalInfo) {
                                    between = isDateBetween(addInfo.daysOffBeginning, rollDate, addInfo.daysOffEnding)
                                }
                            }

                            if (!between && !Object.keys(normalizedRoll).includes(employee)) {
                                normalizedRoll[employee] = status
                            }

                            if (!between && (!Object.keys(normalizedRoll).includes(employee) || status === "PRESENTE")) {
                                normalizedRoll[employee] = status
                            }
                        }
                    }
                }

                if (Object.keys(normalizedRoll).includes('presentPeople')) {
                    tempPresentEmployees += normalizedRoll['presentPeople']
                    tempTotalEmployees += normalizedRoll['totalofPeople']

                    rollsPercentage[depName] = calculatePercentageWithBonus(tempPresentEmployees, tempTotalEmployees)
                } else {
                    const statusList = Object.values(normalizedRoll)

                    const present = statusList.filter(status => status === 'PRESENTE' || status === true).length

                    rollsPercentage[depName] = calculatePercentageWithBonus(present, statusList.length)

                    tempPresentEmployees += present
                    tempTotalEmployees += statusList.length
                }
            })

            let overallTotal = 0
            const totalsByDep = Object.entries(tempAllRolls).reduce((acc, [depName, value]) => {
                let total = 0

                for (const [rollDate, roll] of Object.entries(value)) {
                    const d = new Date(rollDate)
                    if(tempAllDates.includes(d.getDate())) {
                        if (roll.presentPeople && roll.totalofPeople) {
                            total += parseInt(roll.presentPeople, 10)
                        } else {
                            total += Object.values(roll).reduce((a, v) => a + (v === 'PRESENTE' ? 1 : 0), 0)
                        }
                    }
                }

                acc[depName] = total
                overallTotal += total
                return acc
            }, {})

            setTotalPresentPerDep(totalsByDep)
            setOverallTotalPresent(overallTotal)

            setShowGeneralReport(true)
            setAllDates(tempAllDates)
            setAllRolls(tempAllRolls)
            setTotalEmployees(tempTotalEmployees)
            setTotalPresent(overallTotal)
            setOverallDepAtendence(rollsPercentage)

            setShowEditingMenu(false)
        }

        let generalButtonClasses = 'd-flex m-1 p-1 justify-content-center align-items-center rounded text-center button disabled'

        return (<>{
            deps.length !== 0 &&
            <>
                {/* Add Observation Modal */}
                <Modal show={obsEditModalVisible} onHide={closeObsEditModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Adicionar Observações</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-center mb-2'><b>Aluno:</b> {selectedEmployee}</div>
                        <div className='text-center'>
                            <p>Escolha uma dentre as opções abaixo e <br />
                                defina as datas de início e fim</p>
                        </div>
                        <div className='d-flex justify-content-center'>
                            <Button
                                variant={selectedObs === 0 ? 'primary' : 'secondary'}
                                onClick={() => setSelectedObs(0)}
                                className='modal-button'
                            >
                                <img
                                    src={sunny_white}
                                    className="img-fluid"
                                />
                            </Button>
                            <Button
                                variant={selectedObs === 1 ? 'primary' : 'secondary'}
                                onClick={() => setSelectedObs(1)}
                                className='modal-button'
                            >
                                <img
                                    src={work_off_white}
                                    className="img-fluid"
                                />
                            </Button>
                            <Button
                                variant={selectedObs === 2 ? 'primary' : 'secondary'}
                                onClick={() => setSelectedObs(2)}
                                className='modal-button'
                            >
                                <img
                                    src={pregnant_woman_white}
                                    className="img-fluid"
                                />
                            </Button>
                        </div>
                        <div className='d-flex justify-content-center mt-3'>
                            <div className='mx-2'>
                                <Form.Label htmlFor='modal-date-input-start'>
                                    Data de Início
                                </Form.Label>
                                <Form.Control
                                    type='date'
                                    className='modal-date-input'
                                    value={minSelectedDate}
                                    max={maxSelectedDate}
                                    onChange={(e) => {
                                        setMinSelectedDate(e.target.value);
                                    }}
                                />
                            </div>

                            <div className='mx-2'>
                                <Form.Label htmlFor='modal-date-input-end'>
                                    Data de Finalização
                                </Form.Label>
                                <Form.Control
                                    type='date'
                                    id='modal-date-input-end'
                                    className='modal-date-input'
                                    value={maxSelectedDate}
                                    min={minSelectedDate}
                                    onChange={(e) => {
                                        setMaxSelectedDate(e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className='d-flex justify-content-center'>
                            {clickedDep && <div className='mt-2'>
                                <Form.Label htmlFor='obsAvailableDateList'>
                                    Data da observação
                                </Form.Label>
                                <Form.Select id='obsAvailableDateList'
                                    value={observationDate}
                                    onChange={(e) => {
                                        setObservationDate(e.target.value);
                                    }}
                                >
                                    {
                                        Object.entries(dashboardData[clickedDep]).map(([key, value]) => {
                                            let displayDate = new Date(key)
                                            const dd = String(displayDate.getDate()).padStart(2, "0")
                                            const mm = String(displayDate.getMonth() + 1).padStart(2, "0")
                                            const yyyy = displayDate.getFullYear()
                                            const HH = displayDate.getHours()
                                            const MM = displayDate.getMinutes()

                                            const optionValue = dateTimeToDateString(value.registeredDateTime);

                                            return <option value={optionValue} key={key}>
                                                {`${dd}/${mm}/${yyyy}`}
                                            </option>
                                        })
                                    }
                                </Form.Select>
                            </div>}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeObsEditModal}>
                            Cancelar
                        </Button>
                        <Button variant="success" onClick={saveObs}>
                            Salvar
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Remove Observation Modal */}
                <Modal show={obsDelModalVisible} onHide={closeObsDelModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Deletar Observação</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className='text-center'>
                        <div>Você está prestes a deletar uma observação.</div>
                        <div className='d-flex justify-content-center'>

                            <Table borderless className='text-start w-50'>
                                <tbody>
                                    <tr>
                                        <th>Aluno</th>
                                        <td>{selectedEmployee}</td>
                                    </tr>
                                    <tr>
                                        <th>Tipo</th>
                                        <td>{selectedAdditionalInfo.additionalInfo}</td>
                                    </tr>
                                    <tr>
                                        <th>Início</th>
                                        <td>{dateTimeToFancyDateString(selectedAdditionalInfo.daysOffBeginning)}</td>
                                    </tr>
                                    <tr>
                                        <th>Fim</th>
                                        <td>{dateTimeToFancyDateString(selectedAdditionalInfo.daysOffEnding)}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="danger" onClick={delObs}>
                            Deletar
                        </Button>
                        <Button variant="secondary" onClick={closeObsDelModal}>
                            Cancelar
                        </Button>
                    </Modal.Footer>
                </Modal>

                <div className='text-center fs-3 fw-bold py-3 rounded button' style={{ "background": "#255683", "color": "#fff" }}>
                    CONTROLE DE PRESENÇA - {selectedCompany}
                </div>
                <Container className='' id='sd1'>
                    <Row className='mb-3'>
                        {
                            deps.map((dep, index) => {
                                let classes = 'd-flex m-1 p-1 justify-content-center align-items-center rounded text-center button'
                                const disabled = Object.keys(dashboardData[dep]).length == 0

                                if (disabled) {
                                    classes = classes + " disabled"
                                } else {
                                    generalButtonClasses = generalButtonClasses.replace("disabled", "")
                                }

                                return <Col id={index} key={`depButton${index}`}
                                    className={classes}
                                    style={{ "background": "#255683", "color": "#fff", "minWidth": "200px", "minHeight": "60px" }}
                                    onClick={() => disabled ? null : setRollDates(dep)}
                                >
                                    {firebaseToString(dep)}
                                </Col>
                            })
                        }
                        <Col className={generalButtonClasses}
                            style={{ "background": "#255683", "color": "#fff", "minWidth": "200px", "minHeight": "60px" }}
                            onClick={() => setGeral()}
                        >
                            Geral
                        </Col>
                    </Row>
                    {globalData.role === 'admin' &&
                        <AdministrativeFunctions />
                    }
                    {!additionalInfoIconsVisible &&
                        <TableLegend />
                    }
                    {!showGeneralReport && <Row>
                        {depDates.length == 0 &&
                            <>
                                <h3>Selecione um departamento</h3>
                                <h5>Departamentos não clicáveis não tem presenças registradas no periodo</h5>
                            </>
                        }
                        {depDates.length > 0 &&
                            <><Table bordered size='sm' className=''>
                                <thead className='fw-bold'>
                                    <tr style={{ "textAlign": "center", "verticalAlign": "middle" }}>
                                        <td rowSpan={2}></td>
                                        <td rowSpan={2} hidden={numericRoll}>ALUNOS "FREQUENTES"</td>
                                        <td style={{ background: "#1f4e78", color: "white" }} colSpan={depDates.length}>
                                            {firebaseToString(clickedDep)}
                                        </td>
                                        <td style={{ background: "#1f4e78", color: "white" }} rowSpan={2} hidden={numericRoll}>
                                            FREQUÊNCIA <br />MENSAL
                                        </td>
                                        <td style={{ background: "#1f4e78", color: "white" }} rowSpan={2} hidden={numericRoll}>
                                            PERCENTUAL POR <br />COLABORADOR
                                        </td>
                                        <td style={{ background: "#1f4e78", color: "white" }} rowSpan={2} hidden={!numericRoll}>
                                            TOTAL
                                        </td>
                                    </tr>
                                    <tr>
                                        {depDates.map((key, index) => {
                                            return <td style={{ background: "#ddebf7", "textAlign": "center", "verticalAlign": "middle" }}>
                                                {key.toString().padStart(2, "0")}
                                            </td>
                                        })}
                                    </tr>
                                </thead>
                                {numericRoll && <tbody>
                                    <tr>
                                        <td style={{ "textAlign": "center", "verticalAlign": "middle", fontWeight: "bold" }}>
                                            Presentes
                                        </td>
                                        {latestDates.map((date, index) => {
                                            const presentPeople = dashboardData[clickedDep][date]['presentPeople']

                                            if (editingMode) {
                                                return <td className='p-0 m-0'>
                                                    {NumericEditingField(date, 'presentPeople')}
                                                </td>
                                            } else {
                                                return <td style={{ "textAlign": "center", "verticalAlign": "middle" }}>
                                                    {presentPeople}
                                                </td>
                                            }
                                        })}
                                        <td>
                                            {latestDates.reduce((sum, date) => {
                                                return sum + parseInt(dashboardData[clickedDep][date]['presentPeople'])
                                            }, 0)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ "textAlign": "center", "verticalAlign": "middle", fontWeight: "bold" }}>
                                            Total de Alunos
                                        </td>
                                        {latestDates.map((date, index) => {
                                            const totalOfPeople = dashboardData[clickedDep][date]['totalofPeople']
                                            if (editingMode) {
                                                return <td className='p-0 m-0'>
                                                    {NumericEditingField(date, 'totalofPeople')}
                                                </td>
                                            } else {
                                                return <td style={{ "textAlign": "center", "verticalAlign": "middle" }}>
                                                    {totalOfPeople}
                                                </td>
                                            }
                                        })}
                                        <td>
                                            {latestDates.reduce((sum, date) => {
                                                return sum + parseInt(dashboardData[clickedDep][date]['totalofPeople'])
                                            }, 0)}
                                        </td>
                                    </tr>

                                </tbody>}
                                {!numericRoll && <tbody>
                                    {employees.map((employee, eIndex) => {
                                        let totalFrequency = 0
                                        let additionalInfoWritten = false

                                        return <tr>
                                            <td style={{ "textAlign": "center", "verticalAlign": "middle" }}>{eIndex + 1}</td>
                                            <td className='d-flex justify-content-between'>
                                                <span
                                                    onClick={() => {
                                                        setSelectedEmployee(employee)
                                                        showObsEditModal()
                                                    }}
                                                >
                                                    {employee}

                                                </span>
                                                {hasAdditionalInfo(employee, additionalInfo) && <i key={employee + eIndex}
                                                    className="bi bi-info-circle me-1"
                                                    onClick={() => {
                                                        setSelectedEmployee(employee)
                                                        showObsEditModal()
                                                    }}
                                                />}
                                            </td>
                                            {latestDates.map((date, dIndex) => {
                                                let uid = dashboardData[clickedDep][date]['uid']

                                                const status = flatRollData[date.getDate()][employee]

                                                const countAttendance = dashboardData[clickedDep][date]['countAttendance']

                                                let count = status && countAttendance

                                                if (count) totalFrequency += 1;

                                                const baseClass = count ? 'filledRoll' : 'unfilledRoll';
                                                const className = editingMode ? `${baseClass} ${baseClass}-hover` : baseClass;

                                                let employeeMissing = false
                                                let additionalInfoReason = undefined
                                                let additionalInfoIcon = undefined
                                                let short = ""
                                                let icon = <></>

                                                if (additionalInfo[employee] && additionalInfo[employee].length > 0) {
                                                    for (const empAddInfo of additionalInfo[employee]) {
                                                        if (isDateBetween(empAddInfo.daysOffBeginning, date, empAddInfo.daysOffEnding)) {
                                                            additionalInfoReason = empAddInfo.additionalInfo
                                                            additionalInfoIcon = empAddInfo.additionalInfoIconPath

                                                            employeeMissing = true

                                                            if (!additionalInfoWritten) {
                                                                short = empAddInfo.additionalInfoShort
                                                                additionalInfoWritten = true
                                                            }
                                                            icon = <img
                                                                src={additionalInfoIcon}
                                                                alt={additionalInfoReason}
                                                                className="img-fluid"
                                                                style={{ width: '24px', height: '24px' }}
                                                            />
                                                        }
                                                    }
                                                }

                                                if (employeeMissing) {
                                                    if (additionalInfoIconsVisible) {
                                                        return <td
                                                            className='text-center border-0'
                                                            style={{ backgroundColor: "#eff7fd" }}
                                                            additionalInfo={additionalInfo[employee]}
                                                            onClick={() => {
                                                                showObsDelModel(employee, additionalInfo[employee])
                                                            }}
                                                        >
                                                            {icon}
                                                        </td>
                                                    }
                                                    else {
                                                        return <td
                                                            className='text-center border-0 fw-bold'
                                                            style={{ backgroundColor: "#eff7fd" }}
                                                            onClick={() => {
                                                                showObsDelModel(employee, additionalInfo[employee])
                                                            }}
                                                        >
                                                            {short}
                                                        </td>
                                                    }
                                                } else {
                                                    return (
                                                        <td
                                                            onClick={editingMode ? () => clickedRoll(selectedCompany, clickedDep, date, employee, uid) : undefined}
                                                            className={className}
                                                        />
                                                    );
                                                }
                                            })}
                                            <td style={{ "textAlign": "center", "verticalAlign": "middle" }}>
                                                {totalFrequency}
                                            </td>
                                            <td style={{ "textAlign": "center", "verticalAlign": "middle" }}>
                                                {((totalFrequency / latestDates.length) * 100).toFixed(0)}%
                                            </td>
                                        </tr>
                                    })}
                                    <tr style={{ "textAlign": "center", "verticalAlign": "middle" }} className='fw-bold'>
                                        <td colSpan={2} style={{ background: "#ddebf7" }}>TOTAL PRESENÇAS</td>
                                        {depDates.map((key, index) => {
                                            if (Object.keys(totalPresentPerDay).includes(key.toString())) {
                                                return <td style={{ background: "#ddebf7" }} >
                                                    {Object.keys(totalPresentPerDay[key]).length}
                                                </td>
                                            } else return <td>0</td>
                                        })}
                                        <td>{totalFrequency}</td>
                                        <td>{totalPercent}%</td>
                                    </tr>
                                    {/* <tr style={{ "textAlign": "center", "verticalAlign": "middle" }} className='fw-bold'>
                                        <td colSpan={2} style={{ background: "#ddebf7" }}>PRESENÇAS POSSÍVEIS</td>
                                        {depDates.map((key, index) => {
                                            return <td style={{ background: "#ddebf7" }} > </td>
                                        })}
                                        <td colSpan={2} style={{ background: "#ddebf7" }}>{depDates.length * employees.length}</td>
                                    </tr> */}
                                </tbody>}
                            </Table>
                                <Row>
                                    {/* <DepCurrentMonthGraph data={overallDepAtendence} /> */}
                                    <MonthToMonthGraph
                                        selectedCompany={selectedCompany}
                                        selectedYear={selectedYear}
                                        selectedDep={clickedDep}
                                        dateStringToDateTime={dateStringToDateTime}
                                        parseAdditionalInfo={parseAdditionalInfo}
                                        parseDaysOff={parseDaysOff}
                                        isDateBetween={isDateBetween}
                                        calculatePercentageWithBonus={calculatePercentageWithBonus}
                                        calculateOverallAtendence={calculateOverallAtendence}
                                        parsePresentAndTotal={parsePresentAndTotal}
                                        validRollKeys={validRollKeys}
                                    />
                                </Row>
                            </>

                        }
                    </Row>
                    }
                    {showGeneralReport && <>
                        <Row>
                            <Table bordered size='sm' className=''>
                                <thead className='fw-bold'>
                                    <tr style={{ "textAlign": "center", "verticalAlign": "middle" }}>
                                        <td style={{ background: "#ddebf7" }} rowSpan={2}>SETORES</td>
                                        <td style={{ background: "#ddebf7" }} colSpan={allDates.length}>
                                            DIAS
                                        </td>
                                        <td style={{ background: "#ddebf7" }} rowSpan={2}>
                                            FREQUÊNCIA <br />TOTAL
                                        </td>
                                        <td style={{ background: "#ddebf7" }} rowSpan={2}>
                                            FREQUÊNCIA %
                                        </td>
                                    </tr>
                                    <tr>
                                        {
                                            allDates.map((key, index) => {
                                                return <td style={{
                                                    background: "#ddebf7",
                                                    "textAlign": "center",
                                                    "verticalAlign": "middle"
                                                }}>
                                                    {key.toString().padStart(2, "0")}
                                                </td>
                                            })
                                        }
                                    </tr>
                                </thead>
                                <tbody style={{ textAlign: "center", verticalAlign: "middle" }}>
                                    {Object.entries(allRolls).map(([dep, rolls], index) => {
                                        let totalPresent = 0
                                        let totalOfPeople = 0

                                        return <tr>
                                            <td>{firebaseToString(dep)}</td>

                                            {allDates.map((date, rollsIndex) => {

                                                for (let [rollDate, rollData] of Object.entries(rolls)) {
                                                    rollDate = new Date(rollDate)
                                                    let totalPresentInDep = 0
                                                    let totalOfPeopleInDep = 0

                                                    if (rollDate.getDate() === date) {
                                                        for (let [employeeName, employeeStatus] of Object.entries(rollData)) {
                                                            if (employeeName === "totalofPeople") {
                                                                totalOfPeopleInDep += parseInt(employeeStatus)
                                                            }
                                                            else if (employeeName === "presentPeople") {
                                                                totalPresentInDep += parseInt(employeeStatus)
                                                            }
                                                            else if (!namesNotToCount.includes(employeeName)) {
                                                                totalOfPeopleInDep += 1

                                                                if (employeeStatus === "PRESENTE") {
                                                                    let count = true

                                                                    if (additionalInfo[employeeName]) {
                                                                        for (const empAddInfo of Object.values(additionalInfo[employeeName])) {
                                                                            count = !isDateBetween(empAddInfo.daysOffBeginning, rollDate, empAddInfo.daysOffEnding)
                                                                        }
                                                                    }

                                                                    if (count) {
                                                                        totalPresentInDep += 1
                                                                    }
                                                                }
                                                            }
                                                        }

                                                        totalPresent += totalPresentInDep
                                                        totalOfPeople += totalOfPeopleInDep
                                                    }

                                                    if (totalOfPeopleInDep > 0) {
                                                        return <td style={{ background: "#ddebf7" }}>{totalPresentInDep}</td>
                                                    }
                                                }

                                                return <td style={{ background: "#ddebf7" }}>-</td>
                                            })}

                                            <td>{totalPresent}</td>

                                            <td>{(overallDepAtendence[dep] * 100).toFixed(0)}%</td>
                                        </tr>
                                    })}
                                    <tr style={{ fontWeight: "bold" }}>
                                        <td>TOTAL</td>
                                        {allDates.map((date, rollsIndex) => {
                                            let totalPresent = Object.entries(allRolls).reduce((acc, [key, value]) => {
                                                for (let [rollDate, roll] of Object.entries(value)) {
                                                    rollDate = new Date(rollDate)

                                                    const rollKeys = Object.keys(roll)

                                                    if (rollDate.getDate() == date) {
                                                        if (rollKeys.includes("presentPeople") && rollKeys.includes("totalofPeople")) {
                                                            acc += parseInt(roll["presentPeople"])
                                                        } else {
                                                            acc += Object.values(roll).reduce((a, v) => {
                                                                return a + (v === "PRESENTE" ? 1 : 0)
                                                            }, 0)
                                                        }
                                                    }
                                                }
                                                return acc
                                            }, 0)

                                            return <td>{totalPresent}</td>
                                        })}

                                        <td>{overallTotalPresent}</td>

                                        <td>{calculateOverallAtendence(overallDepAtendence)}%</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Row>
                        <Row className='mb-2'>
                            <DepCurrentMonthGraph data={overallDepAtendence} />
                            <MonthToMonthGraph
                                selectedCompany={selectedCompany}
                                selectedYear={selectedYear}
                                dateStringToDateTime={dateStringToDateTime}
                                parseAdditionalInfo={parseAdditionalInfo}
                                parseDaysOff={parseDaysOff}
                                isDateBetween={isDateBetween}
                                calculatePercentageWithBonus={calculatePercentageWithBonus}
                                calculateOverallAtendence={calculateOverallAtendence}
                                parsePresentAndTotal={parsePresentAndTotal}
                                validRollKeys={validRollKeys}
                            />
                        </Row>
                    </>}
                </Container>
            </>
        }</>);
    }
}

export default Dashboard